import { useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { ErrorMessage } from '@hookform/error-message';
import { Box, Text } from '@theme-ui/components';
import { css } from '@emotion/react';

import Checkbox from '@app/components/input/Checkbox';
import TextArea from '@app/components/input/TextArea';
import Loader from '@app/components/Loader';
import { RadioBoxRadio } from '@app/components/RadioBox';
import theme from '@app/styles/theme';
import { rem } from '@app/styles/theme/layout';

import type { OrderHistoryProductFragment } from '../../__graphql__/OrderHistory';
import messages from '../ReturnRequest.messages';
import type { ReturnRequestFormData } from './ReturnRequestForm';

export const FormErrorMessage = ({
  fieldName,
}: {
  fieldName: keyof ReturnRequestFormData;
}) => {
  const {
    formState: { errors },
  } = useFormContext();

  return (
    <ErrorMessage
      name={fieldName}
      errors={errors}
      data-testid={`return-request-validation-${fieldName}`}
      render={({ message }) => (
        <Box mb={32}>
          <Text sx={{ color: `${theme.colors.red}`, fontSize: rem(14.4) }}>
            {message}
          </Text>
        </Box>
      )}
    />
  );
};

export const RemainingChars = ({
  charsRemaining,
}: {
  charsRemaining: number;
}) => {
  return (
    <Text
      sx={{
        color: !charsRemaining ? `${theme.colors.red}` : 'unset',
        fontSize: rem(12),
        fontStyle: 'italic',
      }}
      data-testid={'return-request-remaning-chars'}
    >
      <FormattedMessage
        {...messages.formReturnInfoRemaining}
        values={{ charsRemaining }}
      />
    </Text>
  );
};

export const AdditionalInfoInput = () => {
  const { register } = useFormContext();
  return (
    <>
      <Text
        sx={{
          fontSize: rem(11),
          position: 'absolute',
          padding: `${rem(8)} ${rem(12)}`,
          zIndex: 1,
        }}
      >
        <FormattedMessage {...messages.formReturnInfoPlaceholder} />
      </Text>

      <TextArea
        rows={4}
        label="return-request-form-additionalInfo"
        style={{
          padding: `${rem(24)} ${rem(12)}`,
          maxWidth: '100%',
          marginBottom: 0,
        }}
        maxLength={1000}
        {...register('additionalInfo', {
          required: {
            value: true,
            message: messages.formValidationInfo.defaultMessage,
          },
        })}
        data-testid={'return-request-form-additionalInfo'}
      />
    </>
  );
};

export const ReturnReasonRadioInput = ({
  returnReason: { label, value },
}: {
  returnReason: {
    attributeCode: string;
    label: string;
    value: string;
  };
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  return (
    <div>
      <RadioBoxRadio
        {...register('returnReason', {
          required: {
            value: true,
            message: messages.formValidationReason.defaultMessage,
          },
        })}
        name="returnReason"
        value={label}
        id={label}
        error={Boolean(errors.returnReason)}
        data-testid={`return-request-form-returnReason-${value}`}
      />
      {/*eslint-disable-next-line jsx-a11y/label-has-associated-control*/}
      <label
        htmlFor={label}
        css={css`
          padding: 0;
          margin: ${rem(12)} 0;
          padding: 0;
          font-size: ${rem(14)};
        `}
      >
        <FormattedMessage id={label} />
      </label>
    </div>
  );
};

export const ProductCheckboxInput = ({
  product,
}: {
  product: OrderHistoryProductFragment | null;
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const returnSubmitted = product?.returnStatus !== null;
  const disabled = !product?.eligibleForReturn || returnSubmitted;

  if (!product) return null;
  return (
    <Box my={12} key={product.id}>
      <label
        css={css`
          font-size: ${rem(14)};
          opacity: ${disabled ? 0.5 : 1};
          cursor: ${disabled ? 'unset' : 'pointer'};
        `}
        htmlFor={product.id}
      >
        <Checkbox
          {...register('productsToReturn', {
            required: {
              value: true,
              message: messages.formValidationProducts.defaultMessage,
            },
          })}
          data-testid={`return-request-form-product-${product.id}`}
          type="checkbox"
          value={product.id}
          disabled={disabled}
          id={product.id}
          aria-invalid={Boolean(errors.productsToReturn)}
        />
        <span>
          {product.productName}{' '}
          {returnSubmitted && (
            <small>
              (<FormattedMessage {...messages.returnRequestSubmitted} />)
            </small>
          )}
        </span>
      </label>
    </Box>
  );
};

export const FormError = () => {
  return (
    <Box
      data-testid={'return-request-form-loading-error'}
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
      }}
    >
      <span>
        <FormattedMessage {...messages.formError} />
      </span>
    </Box>
  );
};

export const FormLoading = () => {
  return (
    <Box
      data-testid={'return-request-form-loading'}
      py={4}
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
        flexGrow: 2,
        minHeight: rem(350),
      }}
    >
      <Loader color={theme.colors.lighterBlue} size={35} />
    </Box>
  );
};
